import React, { memo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Alert,
  AlertTitle,
  Typography,
  ButtonGroup,
  Divider,
} from "@mui/material";
import { Add, History, ArrowBack } from "@mui/icons-material";

import NewRunWizard from "./components/NewRunWizard";
import PreviousRunsList from "./components/PreviousRunsList";
import ResultsDialog from "./components/ResultsDialog";
import { VIEW_STATES } from "./constants/drawdownConstants";

/**
 * Drawdown Tool control panel content
 */
const DrawdownToolControl = memo(({ isMapToolEnabled, onMapToolToggle }) => {
  const [currentView, setCurrentView] = useState(VIEW_STATES.MAIN);
  const [selectedRunNdx, setSelectedRunNdx] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [autofillData, setAutofillData] = useState(null);

  const handleViewPreviousRun = (runNdx) => {
    setSelectedRunNdx(runNdx);
    setShowResults(true);
  };

  const handleRunComplete = (runNdx) => {
    setSelectedRunNdx(runNdx);
    setShowResults(true);
    setCurrentView(VIEW_STATES.MAIN);
  };

  const handleAutofillRun = (runDetails) => {
    setAutofillData(runDetails);
    setShowResults(false);
    setCurrentView(VIEW_STATES.NEW_RUN);
  };

  const renderMainView = () => (
    <Box display="flex" flexDirection="column" gap={2}>
      <Alert severity="info" sx={{ mb: 1 }}>
        <AlertTitle>How to Use</AlertTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          The Drawdown Tool analyzes the potential impact of a proposed well on
          existing wells in the area.
        </Typography>
        <Typography variant="body2">
          Choose to create a new analysis or view results from previous runs.
        </Typography>
      </Alert>

      <Typography variant="h6" sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
        Select an Option
      </Typography>

      <ButtonGroup orientation="vertical" fullWidth variant="outlined">
        <Button
          onClick={() => {
            setCurrentView(VIEW_STATES.NEW_RUN);
            setAutofillData(null); // Clear any existing autofill data
          }}
          startIcon={<Add />}
          sx={{ justifyContent: "flex-start", py: 1.5 }}
        >
          New Analysis Run
        </Button>
        <Button
          onClick={() => setCurrentView(VIEW_STATES.PREVIOUS_RUNS)}
          startIcon={<History />}
          sx={{ justifyContent: "flex-start", py: 1.5 }}
        >
          View Previous Runs
        </Button>
      </ButtonGroup>
    </Box>
  );

  const renderBackButton = () => (
    <Box sx={{ mb: 2 }}>
      <Button
        onClick={() => {
          setCurrentView(VIEW_STATES.MAIN);
          setAutofillData(null); // Clear autofill data when going back
        }}
        size="small"
        sx={{ mb: 1 }}
        startIcon={<ArrowBack />}
      >
        Back to Main
      </Button>
      <Divider />
    </Box>
  );

  return (
    <Box display="flex" flexDirection="column">
      {currentView === VIEW_STATES.MAIN && renderMainView()}

      {currentView === VIEW_STATES.NEW_RUN && (
        <Box>
          {renderBackButton()}
          <NewRunWizard
            onRunComplete={handleRunComplete}
            isMapToolEnabled={isMapToolEnabled}
            onMapToolToggle={onMapToolToggle}
            autofillData={autofillData}
          />
        </Box>
      )}

      {currentView === VIEW_STATES.PREVIOUS_RUNS && (
        <Box>
          {renderBackButton()}
          <PreviousRunsList onSelectRun={handleViewPreviousRun} />
        </Box>
      )}

      <ResultsDialog
        open={showResults}
        onClose={() => setShowResults(false)}
        runNdx={selectedRunNdx}
        onAutofill={handleAutofillRun}
      />
    </Box>
  );
});

DrawdownToolControl.displayName = "DrawdownToolControl";

DrawdownToolControl.propTypes = {
  isMapToolEnabled: PropTypes.bool.isRequired,
  onMapToolToggle: PropTypes.func.isRequired,
};

export default DrawdownToolControl;
